import styled from "styled-components"
import { insetPaddingLeftCss, insetPaddingRightCss } from "@/styles/globalStyles"
import { getCssSizeValue, getHtmlFromRedactorField, getPaddingsClassFromData } from "@/utils/utils"
import GridContainer from "@/components/common/GridContainer"
import Reveal from "@/animations/Reveal"
import { BUTTON_VARIANTS } from "@/constants"
import React from "react"
import useDevice, { useIsMobile } from "@/hooks/useDevice"
import { BREAKPOINTS } from "@/styles/themeSettings"
import LinkButton from "@/components/common/Buttons/LinkButton"
import HeaderDescriptionBlock from "@/components/HeaderDescriptionBlock"

const StyledWrapper = styled.div`
	${insetPaddingLeftCss};
	${insetPaddingRightCss};
	display: flex;
	justify-content: center;
	background: ${({ theme }) => theme.colors.periwinkle};
	padding: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)} 0;

	.grid-container {
		height: 100%;
		align-items: center;
	}

	.button-wrapper {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: flex-end;
		margin-top: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)};
		@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
			margin-bottom: unset;
		}
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		justify-content: flex-start;
		padding: ${({ $device, $insidePaddingsSize }) => getCssSizeValue(getPaddingForDevice($device, $insidePaddingsSize), $device)} 0;
		.button-wrapper {
			margin-top: unset;
		}
	}
`

const VARIANTS = {
	TEXT: "text",
	TEXT_AND_BUTTON: "text_and_button",
}

const SIZE_VARIANT = {
	SMALL: "small",
	MEDIUM: "medium",
	LARGE: "large",
}

const sizePaddingsValues = {
	[BREAKPOINTS.DESKTOP]: {
		small: 21.5,
		medium: 64,
		large: 80,
	},
	[BREAKPOINTS.TABLET]: {
		small: 48,
		medium: 64,
		large: 72,
	},
	[BREAKPOINTS.MOBILE]: {
		small: 24,
		medium: 24,
		large: 32,
	},
}

const fontsSizeValues = {
	[SIZE_VARIANT.LARGE]: "typo-borna-106",
	[SIZE_VARIANT.MEDIUM]: "typo-borna-106",
	[SIZE_VARIANT.SMALL]: "typo-borna-122",
}

const getPaddingForDevice = (device, size) => {
	return sizePaddingsValues[device]?.[size] || sizePaddingsValues.desktop.large
}

const getFontClassName = (size) => {
	return fontsSizeValues[size] || fontsSizeValues[SIZE_VARIANT.LARGE]
}

export default function HighlightedHeading({ data }) {
	const isMobile = useIsMobile()
	const highlightedHeading = data?.highlightedHeadingBlock || {}
	const id = highlightedHeading?.id || ""
	const content = highlightedHeading?.content || ""
	const paddings = getPaddingsClassFromData(highlightedHeading)
	const variant = highlightedHeading?.variant || VARIANTS.TEXT

	const { device } = useDevice()

	const insidePaddingsSize = highlightedHeading?.sizeVariant || SIZE_VARIANT.LARGE
	const font = getFontClassName(insidePaddingsSize)

	return (
		<div id={id} className={paddings}>
			<HeaderDescriptionBlock headerDescription={data?.headerDescription} />
			<StyledWrapper $device={device} $insidePaddingsSize={insidePaddingsSize}>
				<GridContainer>
					<Reveal
						onInView
						className={`${variant === VARIANTS.TEXT ? "col-desktop-12 col-tablet-7 col-mobile-8" : "col-desktop-10 col-tablet-6 col-mobile-8"}`}
					>
						<div dangerouslySetInnerHTML={getHtmlFromRedactorField(content)} className={`typo-borna ${font} header`} />
					</Reveal>
					{variant === VARIANTS.TEXT_AND_BUTTON && (
						<>
							{!isMobile && <div className="col-desktop-3" />}
							<div className="col-desktop-3 button-wrapper">
								<LinkButton data={highlightedHeading?.button} variant={BUTTON_VARIANTS.QUATERNARY} />
							</div>
						</>
					)}
				</GridContainer>
			</StyledWrapper>
		</div>
	)
}
